import React, { useEffect, useMemo } from "react";
import FormItem from "@components/FormItem/FormItem";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { zodResolver } from "@hookform/resolvers/zod";
import { convertUaeToUtc, getCurrentUaeDate } from "@utils/date-time";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  ACTIVITY_TYPES,
  getActivitySchema,
  LIMITS,
  type ActivityFormData,
  type StoredActivity,
} from "../schema/dryBulkNominationSchema";
import { toUaeWallClockDate } from "../utils/uaeDate";
import DryBulkDrawerFields from "./DryBulkDrawerFields";

const LANG_BASE = "vessel-nomination.dry-bulk.add.activities";

type DrawerValues = Omit<ActivityFormData, "dateFrom" | "dateTo"> & {
  dateFrom: Date | null;
  dateTo: Date | null;
};
const EMPTY: DrawerValues = {
  activityType: "" as ActivityFormData["activityType"],
  otherText: "",
  dateFrom: null,
  dateTo: null,
};

interface Props {
  isOpen: boolean;
  activity?: StoredActivity;
  onClose: () => void;
  onSave: (activity: StoredActivity) => void;
}

/** VN-07: fixed activity list, "Other" needs a text (≤ 30); dates cannot be in the past and To ≥ From. */
const ActivityDrawer: React.FC<Props> = ({ isOpen, activity, onClose, onSave }) => {
  const { t } = useTranslation();
  const schema = useMemo(() => getActivitySchema(t), [t]);
  const form = useForm<DrawerValues, undefined, ActivityFormData>({
    mode: "onTouched",
    resolver: zodResolver(schema) as never,
    defaultValues: EMPTY,
  });

  useEffect(() => {
    if (!isOpen) return;
    form.reset(
      activity
        ? {
            ...activity,
            otherText: activity.otherText ?? "",
            dateFrom: toUaeWallClockDate(activity.dateFrom),
            dateTo: toUaeWallClockDate(activity.dateTo),
          }
        : EMPTY,
    );
  }, [form, isOpen, activity]);

  const activityType = form.watch("activityType");
  const options = ACTIVITY_TYPES.map((value) => ({
    value,
    label: t(`${LANG_BASE}.types.${value}`),
  }));

  const save = form.handleSubmit((values) =>
    onSave({
      activityType: values.activityType,
      otherText: values.activityType === "OTHER" ? values.otherText : "",
      dateFrom: convertUaeToUtc(values.dateFrom),
      dateTo: convertUaeToUtc(values.dateTo),
    }),
  );

  return (
    <div data-testid="dry-bulk-activity-drawer">
      <FormProvider {...form}>
        <SideDrawer
          isOpen={isOpen}
          onClose={onClose}
          onSubmit={save}
          title={t(`${LANG_BASE}.${activity ? "edit" : "add"}`)}
          submitLabel={t(`${LANG_BASE}.${activity ? "save" : "add-submit"}`)}
          cancelLabel={t("common.cancel")}
        >
          <DryBulkDrawerFields testId="dry-bulk-activity-fields">
            <FormItem<DrawerValues, "activityType">
              required
              testId="dry-bulk-activity-type"
              name="activityType"
              type="select"
              label={t(`${LANG_BASE}.type`)}
              options={options}
            />
            {activityType === "OTHER" && (
              <FormItem<DrawerValues, "otherText">
                required
                testId="dry-bulk-activity-other-text"
                name="otherText"
                label={t(`${LANG_BASE}.other-text`)}
                maxChars={LIMITS.OTHER_TEXT}
                showCharCounter
              />
            )}
            <FormItem<DrawerValues, "dateFrom">
              required
              testId="dry-bulk-activity-date-from"
              name="dateFrom"
              type="date-picker"
              timePicker
              combinedInput
              minDate={getCurrentUaeDate()}
              label={t(`${LANG_BASE}.date-from`)}
            />
            <FormItem<DrawerValues, "dateTo">
              required
              testId="dry-bulk-activity-date-to"
              name="dateTo"
              type="date-picker"
              timePicker
              combinedInput
              minDate={getCurrentUaeDate()}
              label={t(`${LANG_BASE}.date-to`)}
            />
          </DryBulkDrawerFields>
        </SideDrawer>
      </FormProvider>
    </div>
  );
};

export default ActivityDrawer;
